import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowRight, ChevronLeft } from 'lucide-react'
import { hasModule } from '../../access/modules.js'
import { useCurrentUser } from '../../../hooks/useCurrentUser.js'
import { PROSPECTION_SOURCES } from '../sources.js'
import Prospects from '../../../pages/Prospects.jsx'
import Immobilier from '../../../pages/Immobilier.jsx'
import FranceTravailSearchPanel from '../../france-travail-candidates/components/FranceTravailSearchPanel.jsx'
import LinkedinSearchPanel from '../../linkedin/components/LinkedinSearchPanel.jsx'

const SOURCE_SCREENS = {
  b2b: () => <Prospects />,
  linkedin: () => <LinkedinSearchPanel />,
  // Les annonces acquises sont dans le CRM (base « Biens immobiliers »).
  immobilier: () => <Immobilier showAcquired={false} />,
  'france-travail': () => <FranceTravailSearchPanel />,
}

function SourceChooser({ sources }) {
  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-2xl font-bold text-ink">Que voulez-vous rechercher ?</h1>
      <p className="mt-2 text-sm text-ink/50">Choisissez une source : le formulaire adapté s'affiche, et les résultats arrivent dans votre CRM.</p>

      {sources.length === 0 ? (
        <p className="mt-8 rounded-2xl border border-ink/10 bg-ink/[0.03] p-6 text-sm text-ink/60">
          Aucune source de recherche n'est activée pour votre compte. Contactez un administrateur.
        </p>
      ) : (
        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {sources.map((source) => {
            const Icon = source.icon
            return (
              <Link
                key={source.key}
                to={`/dashboard/prospection/${source.key}`}
                className="group flex flex-col rounded-[24px] border border-ink/10 bg-ink/[0.03] p-6 transition hover:border-[#5b5cf4]/40 hover:bg-[#5b5cf4]/[0.04]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#5b5cf4]/10 text-[#5b5cf4] ring-1 ring-[#5b5cf4]/20">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">{source.label}</span>
                <span className="mt-1 text-lg font-bold text-ink">{source.title}</span>
                <span className="mt-2 flex-1 text-sm leading-6 text-ink/50">{source.description}</span>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#5b5cf4]">
                  Lancer une recherche <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Prospection : une seule entrée pour toutes les recherches.
 *   /dashboard/prospection           -> choix de la source
 *   /dashboard/prospection/:source   -> formulaire et suite de cette source
 */
export default function ProspectionPage() {
  const { source: sourceKey } = useParams()
  const { user, isLoading } = useCurrentUser()

  if (isLoading) return null

  const sources = PROSPECTION_SOURCES.filter((s) => hasModule(user, s.module))
  if (!sourceKey) return <SourceChooser sources={sources} />

  const source = sources.find((s) => s.key === sourceKey)
  if (!source) return <Navigate to="/dashboard/prospection" replace />

  const Screen = SOURCE_SCREENS[source.key]

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Link
          to="/dashboard/prospection"
          className="inline-flex items-center gap-1 rounded-full border border-ink/10 px-3 py-1.5 text-sm text-ink/60 transition hover:border-ink/20 hover:text-ink"
        >
          <ChevronLeft className="h-4 w-4" />
          Sources
        </Link>
        <div className="flex flex-wrap gap-2">
          {sources.map((s) => (
            <Link
              key={s.key}
              to={`/dashboard/prospection/${s.key}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                s.key === source.key
                  ? 'bg-[#5b5cf4] text-white'
                  : 'border border-ink/10 text-ink/60 hover:border-ink/20 hover:text-ink'
              }`}
            >
              {s.label}
            </Link>
          ))}
        </div>
        <Link
          to={`/dashboard/crm/${source.crmBase}`}
          className="ml-auto inline-flex items-center gap-2 text-sm font-semibold text-[#5b5cf4] hover:underline"
        >
          Voir les résultats dans le CRM <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <Screen />
    </div>
  )
}
